import { useEffect, useState } from "react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "./ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Fallback shown until the API responds — same first four items an admin
// gets on a fresh install, so the section is never empty during hydration.
const FALLBACK_FAQS = [
  {
    id: "turnaround",
    q: "How fast will I receive my first draft?",
    a: "Your first draft is with you within **24 to 48 hours** of your order being confirmed.",
  },
  {
    id: "guarantee",
    q: "What is the 60-Day Interview Guarantee?",
    a: "If you don't land an interview within **60 days** of receiving your final CV, we'll rewrite it for free.",
  },
];

const MDLink = (props) => (
  <a
    {...props}
    className="text-brand underline hover:no-underline"
    target="_blank"
    rel="noopener noreferrer"
  />
);
const MDStrong = (props) => (
  <strong {...props} className="font-semibold text-foreground" />
);
const MD_COMPONENTS = { a: MDLink, strong: MDStrong };

export default function FAQ() {
  const [items, setItems] = useState(FALLBACK_FAQS);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/settings/public`)
      .then(({ data }) => {
        if (cancelled) return;
        const faqs = data?.faqs;
        if (Array.isArray(faqs) && faqs.length) setItems(faqs);
      })
      .catch(() => { /* keep the fallback */ });
    return () => { cancelled = true; };
  }, []);

  return (
    <section
      id="faq"
      data-testid="faq-section"
      className="py-24 md:py-32 bg-muted/30"
    >
      <div className="max-w-4xl mx-auto px-6 md:px-10">
        <div className="text-center mb-10 max-w-2xl mx-auto">
          <p className="label-eyebrow">FAQ</p>
          <h2 className="font-serif text-3xl md:text-5xl font-bold mt-3 leading-[1.05]">
            Everything you might be wondering.
          </h2>
        </div>
        <Accordion type="single" collapsible className="space-y-3">
          {items.map((item) => (
            <AccordionItem
              key={item.id}
              value={item.id}
              data-testid={`faq-item-${item.id}`}
              className="rounded-2xl border border-border bg-card px-5 md:px-6"
            >
              <AccordionTrigger
                data-testid={`faq-trigger-${item.id}`}
                className="text-left font-serif text-lg md:text-xl font-semibold hover:no-underline py-5"
              >
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="text-sm md:text-base text-foreground/80 leading-relaxed pb-6">
                <div className="faq-answer space-y-3">
                  <ReactMarkdown components={MD_COMPONENTS}>
                    {item.a || ""}
                  </ReactMarkdown>
                </div>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}
